/**
 * Parsea texto extraído de un recibo/ticket para obtener el monto total.
 * Soporta: S/ 45.50, US$ 35.39, Yape, Plin, etc.
 */

// Monto después de S/, $, etc. (prioridad para Yape/Plin)
const CURRENCY_AMOUNT_REGEX = /(?:S\/?\.?|US\s*\$|USD|\$)\s*([\d]{1,3}(?:[.,]\d{3})*[.,]\d{1,2}|[\d]+[.,]\d{1,2})/gi;
// Monto ANTES de Bs (Venezuela: "22.163,00 Bs" - punto=miles, coma=decimales)
const BS_AMOUNT_REGEX = /([\d]{1,3}(?:\.\d{3})*,\d{1,2})\s*Bs\.?/gi;
// Monto genérico
const PLAIN_AMOUNT_REGEX = /([\d]{1,3}(?:[.,]\d{3})*[.,]\d{1,2}|[\d]+[.,]\d{1,2})\s*(?:S\/?\.?|US\s*\$|USD|\$|Bs\.?)?/g;

const TOTAL_KEYWORDS = /\b(total|total a pagar|grand total|suma total|consumo|pagado)\b/i;

/** ¿Parece fecha (19.03) o número de operación (30895830)? */
function isNoise(num) {
  if (num == null) return true;
  if (num >= 1000000) return true; // Números de operación muy largos
  if (num < 0.01 || num > 500000) return true; // VEF puede tener montos altos
  return false;
}

/** ¿Tiene exactamente 2 decimales en el string original? (24.50 sí, 124.5 no) */
function hasTwoDecimalsInStr(str) {
  if (!str || typeof str !== "string") return false;
  const m = str.match(/[.,](\d{1,2})$/);
  return m != null && m[1].length === 2;
}

/**
 * Corrige error común de OCR: lee "S/" como "51" (S→5, /→1).
 * Solo aplica cuando hay decimales para no dañar montos como S/ 129.
 * Ej: 5124.50 → 24.50, 124.5 → 24.5. Pero 5129 → 129, 129 → 129 (sin cambio).
 */
function correctSslashMisread(rawStr) {
  if (!rawStr || typeof rawStr !== "string") return null;
  const s = rawStr.trim();
  let corrected = null;
  const hasDecimal = /[.,]\d{1,2}$/.test(s);
  if (s.startsWith("51") && s.length > 2 && hasDecimal) {
    const rest = s.slice(2).replace(",", ".");
    const num = parseFloat(rest);
    if (!isNaN(num) && num >= 0.01 && num <= 5000) corrected = num;
  } else if (s.startsWith("5") && s.length > 1 && !hasDecimal) {
    const rest = s.slice(1).replace(",", ".");
    const num = parseFloat(rest);
    if (!isNaN(num) && num >= 1 && num <= 50000) corrected = num;
  } else if (s.startsWith("1") && hasDecimal && /^1\d{2}[.,]\d{1,2}$/.test(s.replace(",", "."))) {
    const rest = s.slice(1).replace(",", ".");
    const dec = rest.split(".")[1] || "";
    if (dec !== "0" && dec !== "00") {
      const num = parseFloat(rest);
      if (!isNaN(num) && num >= 0.01 && num <= 5000) corrected = num;
    }
  }
  return corrected;
}

function parseAmountString(str) {
  if (!str || typeof str !== "string") return null;
  const s = str.trim();
  let normalized;
  if (/,\d{1,2}$/.test(s)) {
    normalized = s.replace(/\./g, "").replace(",", ".");
  } else {
    normalized = s.replace(/,/g, "");
  }
  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
}

function extractAmounts(text) {
  const amounts = [];
  const seen = new Set();
  let match;
  const currencyRegex = new RegExp(CURRENCY_AMOUNT_REGEX.source, "gi");
  while ((match = currencyRegex.exec(text)) !== null) {
    const num = parseAmountString(match[1]);
    if (num !== null && !isNoise(num) && !seen.has(num)) {
      amounts.push({ num, fromCurrency: true });
      seen.add(num);
    }
  }
  const bsRegex = new RegExp(BS_AMOUNT_REGEX.source, "gi");
  while ((match = bsRegex.exec(text)) !== null) {
    const num = parseAmountString(match[1]);
    if (num !== null && !seen.has(num)) {
      amounts.push({ num, fromCurrency: true });
      seen.add(num);
    }
  }
  const plainRegex = new RegExp(PLAIN_AMOUNT_REGEX.source, "g");
  while ((match = plainRegex.exec(text)) !== null) {
    const num = parseAmountString(match[1]);
    if (num !== null && !isNoise(num) && !seen.has(num)) {
      amounts.push({ num, fromCurrency: false });
      seen.add(num);
    }
  }
  return amounts;
}

/**
 * Busca el monto total. Prioriza:
 * 1) Monto junto a S/ en la misma línea (típico de Yape/Plin)
 * 2) Líneas con "Total"
 * 3) Monto con 2 decimales (más confiable)
 * 4) El más pequeño entre candidatos razonables (OCR suele añadir dígitos)
 */
export function parseReceiptAmount(text) {
  if (!text || typeof text !== "string") return null;
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // 1. Montos en líneas con S/, $, Bs (o "51" cuando OCR lee S/ como 51). Preferir 2 decimales o el menor.
  const currencyLineAmounts = [];
  for (const line of lines) {
    const regex = /(?:S\/?\.?|US\s*\$|USD|\$|51)\s*([\d]{1,3}(?:[.,]\d{3})*[.,]\d{1,2}|[\d]+[.,]\d{1,2})/gi;
    let m;
    while ((m = regex.exec(line)) !== null) {
      const raw = m[1];
      const num = parseAmountString(raw);
      if (num !== null && !isNoise(num)) currencyLineAmounts.push({ num, raw });
    }
    const bsMatch = line.match(/([\d]{1,3}(?:\.\d{3})*,\d{1,2})\s*Bs\.?/i);
    if (bsMatch) {
      const num = parseAmountString(bsMatch[1]);
      if (num !== null) currencyLineAmounts.push({ num, raw: bsMatch[1] });
    }
    // OCR lee S/ como 51: "5124.50" o "1" como /: "124.5". Corregir.
    const misreadMatch = line.match(/\b(51[\d]+[.,]\d{1,2}|1\d{2}[.,]\d{1,2})\b/);
    if (misreadMatch) {
      const corrected = correctSslashMisread(misreadMatch[1]);
      if (corrected !== null) currencyLineAmounts.push({ num: corrected, raw: misreadMatch[1], corrected: true });
    }
  }
  if (currencyLineAmounts.length > 0) {
    const withTwo = currencyLineAmounts.filter((a) => hasTwoDecimalsInStr(a.raw) || a.corrected);
    const best = withTwo.length > 0 ? withTwo : currencyLineAmounts;
    return Math.min(...best.map((a) => a.num));
  }

  // 2. Línea con "Total"
  for (const line of lines) {
    if (TOTAL_KEYWORDS.test(line)) {
      const amounts = extractAmounts(line).map((a) => a.num);
      if (amounts.length > 0) return amounts[amounts.length - 1];
    }
  }

  // 3. Entre todos los montos: aplicar corrección S/→51 y preferir el más pequeño
  const all = extractAmounts(text).map((a) => a.num);
  const misreadMatches = text.match(/\b(51[\d]+[.,]\d{1,2}|1\d{2}[.,]\d{1,2})\b/g) || [];
  for (const raw of misreadMatches) {
    const c = correctSslashMisread(raw);
    if (c !== null) all.push(c);
  }
  const filtered = [...new Set(all)].filter((a) => !isNoise(a));
  if (filtered.length > 0) return Math.min(...filtered);
  return null;
}

/**
 * Retorna candidatos para que el usuario elija si el principal falla.
 * Incluye correcciones por S/→51 (ej: 5124.50 → 24.50).
 */
export function parseReceiptAmountCandidates(text) {
  if (!text || typeof text !== "string") return [];
  const amounts = extractAmounts(text).map((a) => a.num);
  const corrected = [];
  const misreadMatches = text.match(/\b(51[\d]+[.,]\d{1,2}|1\d{2}[.,]\d{1,2})\b/g) || [];
  for (const raw of misreadMatches) {
    const c = correctSslashMisread(raw);
    if (c !== null) corrected.push(c);
  }
  const unique = [...new Set([...amounts, ...corrected])].filter((a) => !isNoise(a)).sort((a, b) => a - b);
  return unique.slice(0, 5);
}

/**
 * Detecta la moneda del recibo (USD, PEN, VEF).
 * Prioridad: USD > VEF (Bs) > PEN (S/).
 */
export function parseReceiptCurrency(text) {
  if (!text || typeof text !== "string") return null;
  const t = text.toUpperCase();
  if (/\bUS\s*\$|\bUSD\b/.test(t)) return "USD";
  if (/\bBS\.?\b/i.test(text)) return "VEF";
  if (/S\/\.?|PEN\b/.test(t)) return "PEN";
  return null;
}
